import CloseIcon from '@mui/icons-material/Close';
import DoneIcon from '@mui/icons-material/Done';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Toolbar,
  Tooltip,
  Typography,
  Zoom,
} from '@mui/material';
import getSymbolFromCurrency from 'currency-symbol-map';
import React, { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import AxiosConfig from '../../../../config/AxiosConfig';
import { checkRoleFi } from '../../../helpers/utils/RoleManager';
import BrandIcon from '../../utils/common/BrandIcon';
import LoadingButton from '../../utils/common/LoadingButton';

const headers = [
  {
    id: 'demande',
    align: 'left',
    label: 'Demande',
  },
  {
    id: 'reference',
    align: 'left',
    label: 'Référence',
  },
  {
    id: 'order',
    align: 'left',
    label: 'Commande',
  },
  {
    id: 'customer',
    align: 'left',
    label: 'Client',
  },
  // {
  //   id: "paymentMethod",
  //   align: "left",
  //   label: "Moyen de paiement",
  // },
  // {
  //   id: "amountTTC",
  //   align: "left",
  //   label: "Montant TTC",
  // },
  // {
  //   id: "amountToRefund",
  //   align: "left",
  //   label: "Montant à rembourser",
  // },
  {
    id: 'amounts',
    align: 'left',
    label: 'Remboursement',
  },
  {
    id: 'justification',
    align: 'left',
    label: 'Justification',
  },
  {
    id: 'author',
    align: 'left',
    label: 'Auteur',
  },
  {
    id: 'treatment',
    align: 'left',
    label: 'Traitement',
  },
  {
    id: 'action',
    align: 'left',
    label: 'Action',
  },
];

const boxStyles = {
  width: '100%', // La Box prend 100% de la largeur disponible
  height: '100%', // La hauteur de la Box s'ajuste automatiquement
  border: '1px solid',
  borderRadius: '5px',
  display: 'flex',
  alignItems: 'center',
  padding: 2,
  justifyContent: 'center', // Centrer le contenu horizontalement
  position: 'relative', // Conserver la position pour l'animation
};

const StatusBox = ({ show, borderColor, backgroundColor, IconComponent }) => (
  <Zoom
    in={show}
    timeout={350}
    style={{
      width: '100%',
      height: '100%',
      transitionDelay: show ? '234ms' : '0ms',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
    }}
  >
    <Box
      sx={{
        ...boxStyles,
        borderColor: borderColor,
        backgroundColor: backgroundColor,
      }}
    >
      <IconComponent sx={{ color: '#fff', fontSize: '1.5rem' }} />
    </Box>
  </Zoom>
);

const DashboardSCRefundRow = ({ refund, mutate }) => {
  const navigate = useNavigate();
  const profile = useSelector((state) => state?.login?.profile);
  const [isValidate, setIsValidate] = useState(false);
  const [isRefused, setIsRefused] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [action, setAction] = useState('');
  const [refusedDescription, setRefusedDescription] = useState('');
  const [tooltipContent, setTooltipContent] = useState('');

  const handleNavigateToProject = (projectId, ticketId) => {
    navigate(`/project/${projectId}/tickets/${ticketId}/actions`);
  };

  const handleValidateRefund = () => {
    setIsValidate(true);
    setIsOpen(false);
    setTimeout(() => {
      setIsLoading(true);
      AxiosConfig.create('put', `/refunds/${refund?._id}`)
        .call({
          status: 'TOVALIDATEFI',
          actionBy: profile?._id,
          serviceName: 'responsable',
          refusedDescription: null,
        })
        .exit((_res) => {
          setIsLoading(false);
          mutate();
        })
        .catch((err) => console.error(err));
    }, 789);
  };

  const handleRefuseRefund = () => {
    setIsRefused(true);
    setIsOpen(false);
    setTimeout(() => {
      setIsLoading(true);
      AxiosConfig.create('put', `/refunds/${refund?._id}`)
        .call({
          status: 'REFUSEDSC',
          actionBy: profile?._id,
          serviceName: 'responsable',
          refusedDescription: refusedDescription,
        })
        .exit((_res) => {
          setIsLoading(false);
          mutate();
        })
        .catch((err) => console.error(err));
    }, 789);
  };

  const handleClickCopy = (e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(
      refund?.isMP && refund?.order?.mpId
        ? refund?.order?.mpId
        : refund?.order?.reference
    );
    setTooltipContent('Numéro de commande copié.');
    setTimeout(() => {
      setTooltipContent('');
    }, 2000);
  };

  return (
    <TableRow
      hover
      tabIndex={-1}
      sx={{ cursor: 'pointer', height: 'auto' }}
      value={refund?._id}
      onClick={() =>
        handleNavigateToProject(
          refund?.ticket?.project?._id,
          refund?.ticket?._id
        )
      }
    >
      <TableCell>
        <Chip
          label={refund?.type}
          color={refund?.type === 'Remboursement' ? 'tertiary' : 'primary'}
          size="small"
          sx={{ width: '9rem', borderRadius: '5px', padding: 1 }}
        />
      </TableCell>

      <TableCell>
        <Chip
          label={refund?.ticket?.reference}
          sx={{
            width: '7rem',
            borderRadius: '5px',
            color: 'black',
            backgroundColor: '#ececec',
            fontSize: '0.9rem',
          }}
        />
      </TableCell>

      <TableCell onClick={(e) => handleClickCopy(e)}>
        <Tooltip title={tooltipContent} followCursor>
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              alignItems: 'center',
            }}
          >
            {refund?.order?.reference ? (
              <>
                <BrandIcon
                  brand={refund?.order?.contact?.store?.brand?.name}
                  type={refund?.order?.contact?.store?.contactType?.codeBO}
                  style={{ width: 'inherit', height: '24' }}
                />
                <Tooltip
                  title={
                    refund?.isMP && refund?.order?.mpId
                      ? refund?.order?.reference
                      : null
                  }
                  arrow
                >
                  {refund?.isMP && refund?.order?.mpId
                    ? refund?.order?.mpId
                    : refund?.order?.reference}
                </Tooltip>
              </>
            ) : (
              <>N./A.</>
            )}
          </Box>
        </Tooltip>
      </TableCell>

      <TableCell>
        {refund?.firstName} {refund?.lastName}
      </TableCell>

      <TableCell>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <Tooltip title="Montant à rembourser" arrow>
            <Typography>
              {refund?.amountToRefund}
              {getSymbolFromCurrency(
                refund?.ticket?.contact?.store?.country?.currency?.code
              )}
            </Typography>
          </Tooltip>

          <Typography> / </Typography>

          <Tooltip title="Montant total de la commande" arrow>
            <Typography>
              {refund?.amountTotal}
              {getSymbolFromCurrency(
                refund?.ticket?.contact?.store?.country?.currency?.code
              )}
            </Typography>
          </Tooltip>

          <Typography> par </Typography>

          <Tooltip title="Méthode de paiement" arrow>
            <Typography>{refund?.paymentMethod}</Typography>
          </Tooltip>
        </Box>
      </TableCell>

      <TableCell>
        <Tooltip title={refund?.description} arrow>
          <Chip
            label={refund?.refundType?.name}
            sx={{
              width: '7rem',
              borderRadius: '5px',
              backgroundColor: '#e5ecf2',
              color: 'black',
              fontSize: '0.9rem',
            }}
          />
        </Tooltip>
      </TableCell>

      <TableCell>
        <Tooltip
          title={`Niveau de permission ${refund?.author?.permission?.level}`}
          arrow
        >
          {refund?.author?.firstName} {refund?.author?.lastName}
        </Tooltip>
      </TableCell>

      <TableCell onClick={(e) => e.stopPropagation()}>
        <Tooltip title={refund?.refusedDescription || ''} arrow>
          <Box
            sx={{
              width: 'auto', // Taille fixe de la box
              height: '2.5rem',
              position: 'relative', // Pour ancrer correctement les composants internes
              border: '1px solid',
              whiteSpace: 'nowrap',
              borderRadius: '5px',
              display: 'flex',
              alignItems: 'center',
              marginTop: '0.4rem',
              padding: 2,
            }}
          >
            {checkRoleFi(profile) && refund?.refusedDescription?.length > 0
              ? `${refund?.validatedBy?.firstName
                  ?.toUpperCase()
                  ?.slice(0, 1)}${refund?.validatedBy?.lastName
                  ?.toUpperCase()
                  ?.slice(0, 2)} : ${refund?.refusedDescription}`
              : refund?.status?.name}
          </Box>
        </Tooltip>
      </TableCell>

      <TableCell onClick={(e) => e.stopPropagation()}>
        {refund?.status?.code === 'WAITINGFI' ? (
          <Typography
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              color: '#ff825d',
              fontSize: '1rem',
            }}
          >
            {`${refund?.validatedBy?.firstName
              ?.toUpperCase()
              ?.slice(0, 1)}${refund?.validatedBy?.lastName
              ?.toUpperCase()
              ?.slice(0, 2)} : ${refund?.refusedDescription}`}
          </Typography>
        ) : null}

        {refund?.status?.code === 'TOVALIDATESC' ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-evenly',
              gap: 1,
            }}
          >
            {isValidate ? (
              <StatusBox
                show={isValidate}
                borderColor="#037836"
                backgroundColor="#037836"
                IconComponent={DoneIcon}
              />
            ) : (
              <LoadingButton
                isLoading={isLoading}
                variant="outlined"
                size="small"
                color="success"
                onClick={() => {
                  setIsOpen(true);
                  setAction('accepter');
                }}
                sx={{ height: '2.5rem', width: '1rem', marginTop: '0.4rem' }}
              >
                <DoneIcon />
              </LoadingButton>
            )}

            {isRefused ? (
              <StatusBox
                show={isRefused}
                borderColor="#b11a06"
                backgroundColor="#b11a06"
                IconComponent={CloseIcon}
              />
            ) : (
              <LoadingButton
                isLoading={isLoading}
                variant="outlined"
                size="small"
                color="error"
                onClick={() => {
                  setIsOpen(true);
                  setAction('refuser');
                }}
                sx={{ height: '2.5rem', width: '1rem', marginTop: '0.4rem' }}
              >
                <CloseIcon />
              </LoadingButton>
            )}
          </Box>
        ) : null}

        <Dialog open={isOpen} onClose={() => setIsOpen(false)}>
          <DialogTitle>{`Êtes vous sûr de vouloir ${action}?`}</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Veuillez valider votre choix. Pas de retour en arrière possible
              une fois accepté.
            </DialogContentText>

            {action === 'refuser' ? (
              <TextField
                autoFocus
                required
                margin="dense"
                label="Description"
                value={refusedDescription}
                type="text"
                fullWidth
                variant="standard"
                onChange={(e) => setRefusedDescription(e.target.value)}
                helperText="Motif - 10 caractères minimum"
              />
            ) : null}
          </DialogContent>

          <DialogActions>
            <Button
              variant="outlined"
              color="error"
              onClick={() => setIsOpen(false)}
            >
              Non
            </Button>
            <Button
              variant="contained"
              color="success"
              onClick={
                action === 'accepter'
                  ? handleValidateRefund
                  : handleRefuseRefund
              }
              disabled={action !== 'accepter' && refusedDescription.length < 10}
            >
              Oui
            </Button>
          </DialogActions>
        </Dialog>
      </TableCell>
    </TableRow>
  );
};

const DashboardSCRefundTableToolbar = (props) => {
  const { loading, validating, titleIcon, nb, _datas, _setDatas } = props;
  return (
    <Toolbar
      sx={{
        pl: { sm: 2 },
        pr: { xs: 1, sm: 1 },
        display: 'flex',
        justifyContent: 'space-between',
      }}
    >
      {titleIcon}
      <Typography
        component={'span'}
        sx={{ flex: '1 1 100%', marginLeft: 2 }}
        variant="h6"
        id="tableTitle"
      >
        Remboursement & gestes co :{' '}
        {loading || validating ? (
          <CircularProgress color="info" size={'0.8em'} />
        ) : (
          nb || 0
        )}
      </Typography>
    </Toolbar>
  );
};

const DashboardSCRefundTableHead = () => {
  return (
    <TableHead sx={{ whiteSpace: 'nowrap' }}>
      <TableRow>
        {headers?.map((header) => (
          <TableCell key={header?.id} align={header?.align}>
            {header?.label}
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
};

const DashboardSCRefundTable = (props) => {
  const {
    loading,
    validating,
    service,
    datas,
    mutate,
    limit,
    setLimit,
    page,
    setPage,
    titleIcon,
    total,
  } = props;

  const handleChangePage = useCallback(
    (event, newPage) => {
      setPage(newPage);
    },
    [limit, datas]
  );

  const handleChangeLimit = useCallback(
    (event) => {
      const updatedLimit = parseInt(event.target.value, 10);
      setLimit(updatedLimit);
      setPage(0);
    },
    [datas]
  );
  return (
    <Paper elevation={24} sx={{ width: '100%', mb: 2 }}>
      <DashboardSCRefundTableToolbar
        loading={loading}
        validating={validating}
        service={service}
        datas={datas}
        titleIcon={titleIcon}
        nb={total}
      />
      <TableContainer>
        <Table sx={{ minWidth: 750 }} size="small">
          <DashboardSCRefundTableHead />
          <TableBody>
            {datas
              ? datas?.map((row) => {
                  return row ? (
                    <DashboardSCRefundRow
                      key={row?._id}
                      refund={row}
                      mutate={mutate}
                    />
                  ) : (
                    <TableRow>
                      {headers?.map((h, idx) => (
                        <TableCell key={idx}>
                          <Skeleton
                            key={idx}
                            variant="text"
                            sx={{ fontSize: '2em' }}
                          />
                        </TableCell>
                      ))}
                    </TableRow>
                  );
                })
              : null}
          </TableBody>
        </Table>
      </TableContainer>
      {datas ? (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
          }}
        >
          {validating && <CircularProgress color="info" size={'1.3em'} />}
          <TablePagination
            rowsPerPageOptions={[10]}
            component="div"
            count={total}
            rowsPerPage={limit}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeLimit}
          />
        </Box>
      ) : null}
    </Paper>
  );
};

export default DashboardSCRefundTable;
